import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { shootCoinsFrom, playCoins, playExplosion, playCelebration } from "@/lib/confetti";
import CoinStreakBanner from "@/components/CoinStreakBanner";

function animateNumber(set, from, to, ms) {
  const start = performance.now();
  const tick = (now) => {
    const p = Math.min(1, (now - start) / ms);
    set(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Exaggerated comic skater: big helmet head, baggy tee, knee pads. Faces right.
function Skater({ gender, crouched }) {
  const girl = gender === "girl";
  const helmet = girl ? "#FF2D95" : "#39FF14";
  const tee = girl ? "#7B2D8B" : "#B3282D";
  const shorts = girl ? "#1A1A1A" : "#2D2D2D";
  return (
    <svg width="110" height="130" viewBox="0 0 110 130" style={{ overflow: "visible" }}>
      <g transform={crouched ? "translate(0 10) scale(1 0.92)" : "none"} style={{ transition: "transform .3s" }}>
        {/* legs — bent skater stance */}
        <path d="M40 92 q-4 14 -8 26" stroke={shorts} strokeWidth="11" fill="none" strokeLinecap="round" />
        <path d="M70 92 q8 12 12 26" stroke={shorts} strokeWidth="11" fill="none" strokeLinecap="round" />
        {/* sneakers */}
        <ellipse cx="30" cy="121" rx="13" ry="6" fill="#FFF" stroke="#111" strokeWidth="2" />
        <ellipse cx="84" cy="121" rx="13" ry="6" fill="#FFF" stroke="#111" strokeWidth="2" />
        {/* knee pads */}
        <circle cx="36" cy="104" r="5.5" fill="#111" />
        <circle cx="76" cy="104" r="5.5" fill="#111" />
        {/* baggy tee */}
        <path d="M36 58 q19 -10 38 0 l4 34 q-23 8 -46 0 z" fill={tee} stroke="#111" strokeWidth="1.5" />
        {/* arms out for balance */}
        <path d="M38 64 q-18 4 -26 -6" stroke={tee} strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M72 64 q18 8 28 2" stroke={tee} strokeWidth="10" fill="none" strokeLinecap="round" />
        <circle cx="11" cy="57" r="5" fill="#FFD9B3" />
        <circle cx="101" cy="67" r="5" fill="#FFD9B3" />
        {/* oversized head */}
        <circle cx="56" cy="34" r="22" fill="#FFD9B3" />
        <circle cx="64" cy="33" r="3" fill="#111" />
        <circle cx="49" cy="33" r="3" fill="#111" />
        <path d="M48 44 q9 7 19 0" stroke="#111" strokeWidth="2.6" fill="none" strokeLinecap="round" />
        {girl && <path d="M34 34 q-4 16 2 26" stroke={helmet} strokeWidth="7" fill="none" strokeLinecap="round" />}
        {/* helmet */}
        <path d="M33 32 a23 23 0 0 1 46 0 q-2 -6 -23 -6 t-23 6z" fill={helmet} stroke="#111" strokeWidth="2" />
        <rect x="30" y="27" width="52" height="7" rx="3.5" fill={helmet} stroke="#111" strokeWidth="1.6" />
        <circle cx="56" cy="16" r="3" fill="#111" opacity="0.35" />
      </g>
    </svg>
  );
}

function Board() {
  return (
    <svg width="96" height="30" viewBox="0 0 96 30" style={{ overflow: "visible" }}>
      <path d="M8 12 q-8 -2 -6 -9 M88 12 q8 -2 6 -9" stroke="#E53935" strokeWidth="8" fill="none" strokeLinecap="round" />
      <rect x="6" y="8" width="84" height="8" rx="4" fill="#E53935" stroke="#7A1815" strokeWidth="1.8" />
      <rect x="14" y="9.5" width="68" height="2.4" rx="1.2" fill="#111" opacity="0.5" />
      <circle cx="26" cy="23" r="6" fill="#FFD600" stroke="#111" strokeWidth="1.8" />
      <circle cx="70" cy="23" r="6" fill="#FFD600" stroke="#111" strokeWidth="1.8" />
    </svg>
  );
}

// Skateboarder coin set: rolls in pushing, pops a huge kickflip over the coin
// counters (coins spray off the board), grinds while the totals count up, then
// carves off the far side of the park.
export default function SkateboarderCelebration({
  coinsAwarded, totalCoins, scorePercent,
  canAdvance, onRetry, onContinue, soundOn = true, hasPrev = false, onPrev, onDone, gender = "boy",
}) {
  const prevTotal = Math.max(0, (totalCoins || 0) - (coinsAwarded || 0));
  const [phase, setPhase] = useState("rollIn"); // rollIn -> ollie -> combo -> tail -> rollOff -> done
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(prevTotal);
  const timers = useRef([]);
  const rootRef = useRef(null);
  const sceneRef = useRef(null);
  const doneRef = useRef(null);
  const airborne = phase === "ollie";
  const combo = phase === "combo";
  const showScore = ["combo", "tail", "rollOff", "done"].includes(phase);

  const burst = () => {
    const r = sceneRef.current?.getBoundingClientRect();
    if (!r) return;
    shootCoinsFrom((r.left + r.width * 0.5) / window.innerWidth, (r.top + r.height * 0.55) / window.innerHeight);
  };

  useEffect(() => {
    const add = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    add(() => {
      setPhase("ollie");
      if (soundOn) playCelebration();
      add(() => { burst(); if (soundOn) playExplosion(); }, 420);
    }, 2300);
    add(() => {
      setPhase("combo");
      animateNumber(setCount, 0, coinsAwarded, 1400);
      animateNumber(setTotal, prevTotal, totalCoins, 1600);
      for (let i = 0; i < 7; i++) add(() => { burst(); if (soundOn && i % 2 === 0) playCoins(5); }, 250 + i * 360);
    }, 3400);
    add(() => { setPhase("tail"); if (soundOn) playCoins(9); }, 6400);
    add(() => { setPhase("rollOff"); if (soundOn) playCoins(6); }, 7300);
    add(() => {
      setPhase("done");
      onDone && onDone();
    }, 8800);
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  const rolling = phase === "rollIn" || phase === "rollOff" || phase === "done";
  const leaving = phase === "rollOff" || phase === "done";
  const charAnim =
    phase === "rollIn" ? { x: 0, transition: { duration: 2.1, ease: "easeOut" } }
    : leaving ? { x: 560, transition: { duration: 2.6, ease: "easeIn" } }
    : { x: 0 };

  return (
    <div ref={rootRef} className="text-center py-2 relative scroll-mt-24" data-testid="skateboarder-celebration">
      {/* the skatepark */}
      <div ref={sceneRef} className="relative mx-auto w-full max-w-md h-60 overflow-hidden rounded-2xl"
        style={{ background: "linear-gradient(180deg,#0E2A3A 0%,#1B4A5E 55%,#12303E 55%,#0A1E28 100%)" }}
        data-testid="skateboarder-scene">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 65% 45% at 50% 25%, rgba(0,229,255,0.22), transparent 70%)" }} />
        {/* graffiti accents */}
        <div className="absolute left-5 top-8 font-display font-extrabold text-2xl select-none" style={{ color: "#CCFF00", opacity: 0.28, transform: "rotate(-8deg)" }}>SKATE</div>
        <div className="absolute right-6 top-16 font-display font-extrabold text-xl select-none" style={{ color: "#FF2D95", opacity: 0.25, transform: "rotate(6deg)" }}>4LIFE</div>
        {combo && (
          <motion.div className="absolute inset-0 pointer-events-none"
            animate={{ opacity: [0.1, 0.4, 0.15, 0.45, 0.1] }} transition={{ duration: 0.7, repeat: 4 }}
            style={{ background: "radial-gradient(ellipse 70% 55% at 50% 45%, rgba(204,255,0,0.2), transparent 70%)" }} />
        )}
        {/* grind rail + floor */}
        <div className="absolute left-6 right-6 rounded-full" style={{ bottom: 26, height: 5, background: "#8FA3AD", boxShadow: "0 3px 0 #4A5A62" }} />
        <div className="absolute left-0 right-0" style={{ bottom: 0, height: 12, background: "#06131A" }} />

        {/* the skater */}
        <motion.div className="absolute" style={{ left: "50%", marginLeft: -55, bottom: 18, width: 110, height: 150 }}
          initial={{ x: -560 }} animate={charAnim} data-testid="skateboarder-character">
          <motion.div
            animate={airborne ? { y: -64, transition: { duration: 0.45, ease: "easeOut" } }
              : combo ? { y: [0, -3, 0], transition: { duration: 0.3, repeat: Infinity } }
              : rolling ? { y: [0, -2.5, 0], transition: { duration: 0.5, repeat: Infinity, ease: "easeInOut" } }
              : { y: 0, transition: { duration: 0.35, ease: "easeIn" } }}
            style={{ position: "relative", width: 110, height: 150 }}
          >
            <div style={{ position: "absolute", bottom: 8, left: 0 }}>
              <motion.div animate={airborne ? { rotate: [0, -14, 8, 0] } : { rotate: 0 }} transition={{ duration: 0.9 }}>
                <Skater gender={gender} crouched={phase === "rollIn"} />
              </motion.div>
            </div>
            {/* the board — kickflips a full 360 under the skater mid-air */}
            <motion.div style={{ position: "absolute", left: 8, bottom: -8, transformOrigin: "48px 12px" }}
              animate={airborne ? { rotate: 360, y: -26, transition: { duration: 0.8, ease: "easeInOut" } }
                : { rotate: 0, y: 0, transition: { duration: 0.3 } }}
              data-testid="skateboarder-board">
              <motion.div animate={rolling ? { rotate: [0, 1.5, 0, -1.5, 0] } : {}} transition={{ duration: 0.4, repeat: rolling ? Infinity : 0 }}>
                <Board />
              </motion.div>
            </motion.div>
            {/* speed lines while rolling */}
            {rolling && (
              <div className="absolute" style={{ top: 60, left: leaving ? -34 : 104 }}>
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} className="absolute rounded-full" style={{ width: 26, height: 3, top: i * 12, background: "#7FD6E8", opacity: 0.7 }}
                    animate={{ x: leaving ? [-6, -30] : [6, 30], opacity: [0.7, 0] }}
                    transition={{ duration: 0.5, delay: i * 0.12, repeat: Infinity }} />
                ))}
              </div>
            )}
            {/* sparks off the rail during the combo */}
            {combo && (
              <div className="absolute" style={{ bottom: 4, left: 30 }}>
                {["✦", "✧", "✦"].map((s, i) => (
                  <motion.span key={i} className="absolute font-bold" style={{ fontSize: 16, left: i * 22, color: i % 2 ? "#CCFF00" : "#00E5FF" }}
                    initial={{ opacity: 0, y: 0 }} animate={{ opacity: [0, 1, 0], y: -22, rotate: i % 2 ? 24 : -24 }}
                    transition={{ duration: 0.7, delay: i * 0.18, repeat: Infinity, repeatDelay: 0.15 }}>
                    {s}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Coin bubbles live BELOW the scene and are always mounted (opacity fade only),
          stacked vertically — the scene above never moves when they appear or count up. */}
      <div className={`flex flex-col items-center gap-1.5 mt-2 transition-opacity duration-500 ${showScore ? "opacity-100" : "opacity-0"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          data-testid="quiz-xp-awarded">
          <span className="text-2xl">🪙</span>
          <span className="text-xl sm:text-2xl leading-none">+{count.toLocaleString()} coins today</span>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          data-testid="coin-grand-total">
          <span className="text-2xl">🪙</span>
          <span className="text-3xl leading-none text-outline-lime">{total.toLocaleString()}</span>
          <span className="text-xl leading-none">total</span>
        </div>
        {scorePercent != null && (
          <div className="text-3xl font-extrabold font-display text-[#8fb300]" data-testid="quiz-score">{scorePercent}%</div>
        )}
      </div>

      {showScore && <CoinStreakBanner celebrationsEnabled={soundOn} compact className="mt-2 max-w-md mx-auto justify-center text-center" />}

      <AnimatePresence>
        {phase === "done" && (
          <motion.div ref={doneRef} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }} data-testid="coin-done" className="scroll-mb-24">
            <p className="text-black font-semibold max-w-md mx-auto mt-2" data-testid="quiz-encouragement">
              Sick kickflip — shred the next lesson!
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {hasPrev && (
                <button data-testid="quiz-prev-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={() => onPrev && onPrev()}>
                  ← Prev
                </button>
              )}
              {onRetry && <button data-testid="quiz-retry-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={onRetry}>Redo</button>}
              {canAdvance && onContinue && (
                <button data-testid="quiz-continue-button" className="btn-primary text-sm px-4 py-2 whitespace-nowrap" onClick={onContinue}>
                  Next →
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
